// Fallback discriminator for the id namespace below, used only if an instance
// somehow sits outside a #shopify-section-* wrapper (e.g. a bare theme-editor
// preview). Never resets, so two instances can't collide within a document.
let hceInstanceSeq = 0;

class HomepageCollectionExperience extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-quick-filter-btn]'));
    this.subButtons = Array.from(this.querySelectorAll('[data-sub-filter-btn]'));
    this.toggleButtons = Array.from(this.querySelectorAll('[data-collection-toggle-btn]'));
    if (!this.buttons.length) return;

    this.activeClass = 'is-active';
    this.gridContainer = this.querySelector('#ProductGridContainer');
    this.titleElement = this.querySelector('[data-quick-filter-title]');
    this.descriptionElement = this.querySelector('[data-quick-filter-description]');

    this.primaryUrl = this.dataset.primaryCollectionUrl || '';
    this.mirrorUrl = this.dataset.mirrorCollectionUrl || '';
    this.collectionSectionKey = this.dataset.collectionSectionId || 'product-grid';
    this.productsPerPage = parseInt(this.dataset.productsPerPage, 10) || 12;

    // Unique per section instance - prefixes every id we inject. See namespaceIds().
    const sectionWrapper = this.closest('[id^="shopify-section-"]');
    this.idNamespace = (sectionWrapper && sectionWrapper.id) || `hce-${(hceInstanceSeq += 1)}`;
    this.activeCollectionTarget = 'primary';

    this.cache = new Map();
    this.sectionIds = new Map();
    this.sectionIdPromises = new Map();
    this.requestCounter = 0;
    this.swapGeneration = 0;
    this.abortController = null;
    // "param=value" filter pairs with zero matches in the current category view,
    // rebuilt from facet counts on each parent switch (see collectEmptyFilterValues).
    this.emptyFilterValues = new Set();

    this.handlePillClick = this.onPillClick.bind(this);
    this.handleSubClick = this.onSubClick.bind(this);
    this.handleToggleClick = this.onToggleClick.bind(this);
    this.handlePaginationClick = this.onPaginationClick.bind(this);

    this.buttons.forEach((btn) => btn.addEventListener('click', this.handlePillClick));
    this.subButtons.forEach((btn) => btn.addEventListener('click', this.handleSubClick));
    this.toggleButtons.forEach((btn) => btn.addEventListener('click', this.handleToggleClick));
    if (this.gridContainer) {
      this.gridContainer.addEventListener('click', this.handlePaginationClick);
    }

    this.updateSubFilterVisibility(this.getActiveParentButton());

    // Discover the real section_id for each collection in the background so
    // the first user click doesn't pay the discovery cost.
    if (this.primaryUrl) this.ensureSectionId(this.primaryUrl);
    if (this.mirrorUrl) this.ensureSectionId(this.mirrorUrl);

    // The grid sits below the hero, so its lazy images would pop in one-by-one as
    // you scroll to it. Force the first row to load up front (see below).
    this.upgradeAboveFoldImages();
    this.fetchInitialGrid();
  }

  disconnectedCallback() {
    this.buttons.forEach((btn) => btn.removeEventListener('click', this.handlePillClick));
    this.subButtons.forEach((btn) => btn.removeEventListener('click', this.handleSubClick));
    this.toggleButtons.forEach((btn) => btn.removeEventListener('click', this.handleToggleClick));
    if (this.gridContainer && this.handlePaginationClick) {
      this.gridContainer.removeEventListener('click', this.handlePaginationClick);
    }
    if (this.abortController) this.abortController.abort();
  }

  getActiveCollectionUrl() {
    return this.activeCollectionTarget === 'mirror' ? this.mirrorUrl : this.primaryUrl;
  }

  getActiveParentButton() {
    return this.buttons.find((btn) => btn.classList.contains(this.activeClass)) || this.getDefaultButton();
  }

  getDefaultButton() {
    return this.buttons.find((btn) => btn.dataset.default === 'true') || this.buttons[0];
  }

  getDefaultSubButton() {
    if (!this.subButtons.length) return null;
    return this.subButtons.find((btn) => btn.dataset.default === 'true') || this.subButtons[0];
  }

  // The server-rendered grid is a plain product loop with no {% paginate %},
  // so it's capped at products_per_page and never includes pagination. On load
  // we reconcile it with the canonical collection section — exactly what a pill
  // or filter click fetches — so the active sub-filter's sort is applied and the
  // pager appears whenever there are more products. Previously this only ran
  // when the default sub-filter carried a `filter.` param, which left the
  // default ALL + Best Sellers view (sort only) without a pager.
  fetchInitialGrid() {
    const activeParent = this.getActiveParentButton();
    const defaultSub = this.getDefaultSubButton();
    // Reconcile silently — the server-rendered grid is already on screen, so
    // suppress the loading overlay (it would fade out fully-rendered content on
    // first paint). User-initiated fetches still show the overlay for feedback.
    this.fetchAndSwap(this.buildSearchParams(activeParent, defaultSub).toString(), {
      showLoading: false,
      refreshSubFilters: true,
    });
  }

  onPillClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains(this.activeClass)) return;

    const subToApply = this.getDefaultSubButton();
    const params = this.buildSearchParams(button, subToApply);

    this.setActiveButton(button);
    this.setActiveSubButton(subToApply);
    this.updateSubFilterVisibility(button);
    this.updateHeadingFromButton(button);
    this.fetchAndSwap(params.toString(), { refreshSubFilters: true });
  }

  onSubClick(event) {
    event.preventDefault();
    const subButton = event.currentTarget;
    if (subButton.classList.contains(this.activeClass)) return;

    const activeParent = this.getActiveParentButton();
    const params = this.buildSearchParams(activeParent, subButton);

    this.setActiveSubButton(subButton);
    this.fetchAndSwap(params.toString());
  }

  onToggleClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains(this.activeClass)) return;
    const target = button.dataset.collectionTarget;
    if (!target) return;
    if (target === 'mirror' && !this.mirrorUrl) return;

    this.activeCollectionTarget = target;

    this.toggleButtons.forEach((btn) => {
      const isActive = btn === button;
      btn.classList.toggle(this.activeClass, isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    const defaultParent = this.getDefaultButton();
    const defaultSub = this.getDefaultSubButton();
    this.setActiveButton(defaultParent);
    this.setActiveSubButton(defaultSub);
    this.updateSubFilterVisibility(defaultParent);
    this.updateHeadingFromButton(defaultParent);

    const params = this.buildSearchParams(defaultParent, defaultSub);
    this.fetchAndSwap(params.toString(), { refreshSubFilters: true });
  }

  buildSearchParams(parentButton, subButton = null) {
    const params = new URLSearchParams();

    if (parentButton) {
      const parentParams = this.parseParams(parentButton.dataset.query);
      parentParams.forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }

    if (subButton) {
      const useAllOverride = parentButton && parentButton.dataset.default === 'true' && subButton.dataset.queryAllOverride;
      const subQuery = useAllOverride ? subButton.dataset.queryAllOverride : subButton.dataset.query;
      const subParams = this.parseParams(subQuery);
      subParams.forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }

    return params;
  }

  parseParams(queryString) {
    return new URLSearchParams(queryString || '');
  }

  setActiveButton(activeButton) {
    if (!activeButton) return;
    this.buttons.forEach((btn) => {
      const isActive = btn === activeButton;
      btn.classList.toggle(this.activeClass, isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  setActiveSubButton(activeSubButton) {
    if (!activeSubButton) return;
    this.subButtons.forEach((btn) => {
      const isActive = btn === activeSubButton;
      btn.classList.toggle(this.activeClass, isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  updateSubFilterVisibility(parentButton) {
    if (!this.subButtons.length || !parentButton) return;
    const hideList = (parentButton.dataset.hideSubfilters || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    this.subButtons.forEach((subBtn) => {
      const label = subBtn.textContent.trim().toLowerCase();
      const hidden = hideList.includes(label) || this.isSubFilterEmpty(subBtn);
      subBtn.style.display = hidden ? 'none' : '';
    });
  }

  // A sub-filter is "empty" when a metafield/tag filter in its query has zero
  // matches in the current category view. `emptyFilterValues` is rebuilt from the
  // facet counts of the latest parent fetch (see collectEmptyFilterValues).
  // Sort-only sub-filters (Best Sellers, New Arrivals) carry no `filter.` param,
  // so they are never treated as empty.
  isSubFilterEmpty(subBtn) {
    // Gated sub-filter (opts in via data-filter-param/value — the same settings
    // that drive the server-side count gate on the collection page, which can't
    // run here because the homepage has no collection.filters). Show it only when
    // its value is present WITH products; hide on a zero count OR total absence
    // (e.g. nothing on sale). Guarded by a non-empty present-set so we never
    // over-hide when the fetch returned no facets at all.
    const gateParam = subBtn.dataset.filterParam;
    const gateValue = subBtn.dataset.filterValue;
    if (gateParam && gateValue) {
      // Keep a gated pill hidden until a fetch's facet data proves its value has
      // products. Before any facet data (initial homepage load, where the server-side
      // gate can't run) treat it as empty — the button ships hidden and reveals only
      // once present, so there's no show-then-hide flash.
      if (!this.presentFilterValues || !this.presentFilterValues.size) return true;
      const gateKey = `${gateParam}=${gateValue}`;
      if (!this.presentFilterValues.has(gateKey)) return true; // absent → no products
      if (this.emptyFilterValues && this.emptyFilterValues.has(gateKey)) return true; // present, count 0
      return false; // present with products → reveal
    }

    if (!this.emptyFilterValues || !this.emptyFilterValues.size) return false;
    const params = new URLSearchParams(subBtn.dataset.query || '');
    for (const [key, value] of params) {
      if (key.indexOf('filter.') === 0 && this.emptyFilterValues.has(`${key}=${value}`)) {
        return true;
      }
    }
    return false;
  }

  // Read per-value product counts from the just-fetched collection section's
  // facets. facets.liquid renders a count-0 (and inactive) value with a `disabled`
  // class on its .facets__label plus a trailing "(0)" — both are checked. Returns
  // a Set of "param=value" strings (e.g. "filter.p.tag=sale") that have no matches
  // in the current category view.
  collectEmptyFilterValues(parsed) {
    const empty = new Set();
    // Also record EVERY rendered facet value so a gated sub-filter (Sale) can be
    // hidden when its value is ABSENT entirely — Shopify omits a filter value with
    // zero products, so "absent" is how "nothing on sale anywhere" shows up. Stored
    // as a side effect (this.presentFilterValues) for isSubFilterEmpty.
    const present = new Set();
    parsed.querySelectorAll('label.facets__label').forEach((label) => {
      const input = label.querySelector('input[name^="filter."]');
      if (!input || !input.name || !input.value) return;
      present.add(`${input.name}=${input.value}`);
      let isEmpty = label.classList.contains('disabled');
      if (!isEmpty) {
        const match = label.textContent.match(/\((\d+)\)/);
        if (match && parseInt(match[1], 10) === 0) isEmpty = true;
      }
      if (isEmpty) empty.add(`${input.name}=${input.value}`);
    });
    this.presentFilterValues = present;
    return empty;
  }

  // Read per-value product counts from the just-fetched collection section's facets,
  // to fill the v3-style superscript pill counts the homepage can't render server-side
  // (collection.filters is empty off the collection page). facets.liquid renders each
  // value's label as "<name> (<count>)" alongside an input[name^="filter."]. Returns a
  // flat Map keyed "param|lowercased-value" plus the collection total (largest integer
  // in the product-count element) for pills with no filter (ALL). Values are compared
  // lowercased; URLSearchParams already decodes + and %XX on the query side, and the
  // browser decodes the facet input's value attribute, so both sides match raw.
  collectPillCounts(parsed) {
    const counts = new Map();
    parsed.querySelectorAll('label.facets__label').forEach((label) => {
      const input = label.querySelector('input[name^="filter."]');
      if (!input || !input.name || !input.value) return;
      const match = label.textContent.match(/\((\d[\d,]*)\)/);
      if (!match) return;
      counts.set(`${input.name}|${input.value.toLowerCase()}`, parseInt(match[1].replace(/,/g, ''), 10));
    });
    let total = null;
    const countEl = parsed.querySelector('#ProductCount, #ProductCountDesktop');
    if (countEl) {
      const nums = countEl.textContent.match(/\d[\d,]*/g);
      if (nums && nums.length) {
        total = Math.max(...nums.map((n) => parseInt(n.replace(/,/g, ''), 10)));
      }
    }
    return { counts, total };
  }

  // Fill each pill's superscript count from the facet data. Mirrors v3's server-side
  // derivation: an explicit count_param/count_value override wins; otherwise sum the
  // counts of each filter.* value in the pill's own query, grouped by param (same-param
  // facets are OR → summed), and take the MIN across distinct params. A pill with no
  // filter.* param (ALL) uses the collection total. A pill whose count is 0/unknown
  // keeps its <sup> hidden (no "0" and no layout shift).
  applyPillCounts(data) {
    if (!data) return;
    this.buttons.forEach((btn) => {
      const sup = btn.querySelector('.collection-quick-filters__count');
      if (!sup) return;
      const a11y = btn.querySelector('[data-count-a11y]');
      let count = null;

      const overrideParam = btn.dataset.countParam;
      const overrideValue = btn.dataset.countValue;
      if (overrideParam && overrideValue) {
        count = data.counts.get(`${overrideParam}|${overrideValue.toLowerCase()}`) || 0;
      } else {
        const params = new URLSearchParams(btn.dataset.query || '');
        const byParam = new Map();
        for (const [key, value] of params) {
          if (key.indexOf('filter.') !== 0) continue;
          const c = data.counts.get(`${key}|${value.toLowerCase()}`) || 0;
          byParam.set(key, (byParam.get(key) || 0) + c);
        }
        count = byParam.size ? Math.min(...byParam.values()) : data.total;
      }

      if (count != null && count > 0) {
        sup.textContent = count;
        sup.hidden = false;
        if (a11y) a11y.textContent = `(${count} products)`;
      } else {
        sup.textContent = '';
        sup.hidden = true;
        if (a11y) a11y.textContent = '';
      }
    });
  }

  updateHeadingFromButton(button) {
    if (!button) return;
    if (this.titleElement) {
      const heading = button.dataset.heading || this.titleElement.dataset.defaultHeading || '';
      this.titleElement.textContent = heading;
    }
    if (this.descriptionElement) {
      const description = button.dataset.description || '';
      this.descriptionElement.textContent = description;
      this.descriptionElement.toggleAttribute('hidden', description === '');
    }
  }

  // Shopify renders sections inside JSON templates with a prefixed ID like
  // `template--<template-id>__<key>`. We can't know that ID server-side from
  // the homepage, so we fetch the full collection page once per collection
  // and extract the real ID from the product-grid element. The result is
  // memoized — subsequent AJAX calls reuse it.
  ensureSectionId(collectionUrl) {
    if (this.sectionIds.has(collectionUrl)) {
      return Promise.resolve(this.sectionIds.get(collectionUrl));
    }
    if (this.sectionIdPromises.has(collectionUrl)) {
      return this.sectionIdPromises.get(collectionUrl);
    }
    const promise = (async () => {
      try {
        const response = await fetch(collectionUrl);
        const text = await response.text();
        const match = text.match(/id="product-grid"\s+data-id="([^"]+)"/);
        const sectionId = (match && match[1]) || this.collectionSectionKey;
        this.sectionIds.set(collectionUrl, sectionId);
        return sectionId;
      } catch (e) {
        return this.collectionSectionKey;
      }
    })();
    this.sectionIdPromises.set(collectionUrl, promise);
    return promise;
  }

  async fetchAndSwap(searchParams, { showLoading = true, refreshSubFilters = false } = {}) {
    const collectionUrl = this.getActiveCollectionUrl();
    if (!collectionUrl) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestCounter;

    if (showLoading) this.setLoading(true);

    try {
      const sectionId = await this.ensureSectionId(collectionUrl);
      if (requestId !== this.requestCounter) return;

      const url = `${collectionUrl}?section_id=${encodeURIComponent(sectionId)}${searchParams ? '&' + searchParams : ''}`;
      let parsed;
      let fromCache = false;
      if (this.cache.has(url)) {
        parsed = this.cache.get(url);
        fromCache = true;
      } else {
        const response = await fetch(url, { signal: this.abortController.signal });
        const text = await response.text();
        parsed = new DOMParser().parseFromString(text, 'text/html');
        if (this.cache.size >= 20) {
          this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(url, parsed);
      }
      if (requestId !== this.requestCounter) return;
      // On a parent switch (or initial load), recompute which sub-filters have
      // zero matches in the new category view, then re-hide their pills. The
      // active parent applies its default sort-only sub, so these facet counts
      // reflect the category alone — what each sub-filter pill should gate on.
      if (refreshSubFilters) {
        this.emptyFilterValues = this.collectEmptyFilterValues(parsed);
        // v3 pills carry a superscript product count. The homepage can't compute it
        // server-side (collection.filters is empty off the collection page), so read
        // it from the just-fetched collection facets. Only trust counts from an
        // unfiltered-by-category view — the initial load and every gender toggle fetch
        // the default (ALL) parent, so its facet counts are the true per-category
        // totals. Category-pill clicks keep those page-load totals (matches v3, whose
        // counts are also fixed at page load), so we don't recompute for them.
        if (this.getActiveParentButton() === this.getDefaultButton()) {
          this.applyPillCounts(this.collectPillCounts(parsed));
        }
      }
      // Cached categories render instantly (no dim/decode); only a fresh fetch gets
      // the background-load treatment.
      this.swapGrid(parsed, fromCache);
      if (refreshSubFilters) {
        this.updateSubFilterVisibility(this.getActiveParentButton());
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('homepage-collection-experience fetch failed:', error);
      // Clear the overlay ONLY on a real error (no swap will happen). On success,
      // swapGrid's apply() drops it atomically with the grid swap — clearing it
      // here (the old finally) yanked the overlay before the new grid was ready,
      // which read as a flicker.
      if (requestId === this.requestCounter && showLoading) this.setLoading(false);
    }
  }

  // Force the first row of product images to load up front instead of lazily, so the
  // below-the-hero grid doesn't pop in one image at a time as you scroll to it. First
  // row = 4 cards on desktop (4-up), 2 on mobile (2-up). card-product already renders
  // the first 2 eager, so on desktop this upgrades cards 3–4. Setting eager on the
  // real card <img> (not a separate preload) is what makes the below-fold image
  // actually paint before you arrive at it.
  upgradeAboveFoldImages() {
    if (!this.gridContainer) return;
    const eagerCount = window.matchMedia('(min-width: 990px)').matches ? 4 : 2;
    const items = Array.from(this.gridContainer.querySelectorAll('#product-grid > li')).slice(0, eagerCount);
    items.forEach((li) => {
      const img =
        li.querySelector('.card-image-slider__list .slider__slide:first-child img') || li.querySelector('img');
      if (img && img.getAttribute('loading') !== 'eager') {
        img.setAttribute('loading', 'eager');
        img.loading = 'eager';
      }
    });
  }

  // Preload + DECODE the incoming grid's above-the-fold images as bare <img> clones
  // (same srcset/sizes → same candidate the real cards use). Resolves once they've
  // decoded (or capMs elapses). Warming the browser's decoded-image cache first means
  // the real cards paint already-decoded instead of blank-then-image — even a cached
  // grid, whose freshly-created decoding="async" <img>s would otherwise re-decode and
  // popcorn. Mirrors FacetFiltersForm.preloadAboveFold on the collection page.
  preloadAboveFold(parsedContainer, capMs) {
    return new Promise((resolve) => {
      let sources = [];
      try {
        let perRow = 4;
        const liveGrid = this.gridContainer && this.gridContainer.querySelector('#product-grid');
        if (liveGrid) {
          const isDesktop = window.matchMedia('(min-width: 990px)').matches;
          const match = liveGrid.className.match(
            isDesktop ? /grid--(\d+)-col-desktop/ : /grid--(\d+)-col-tablet-down/
          );
          if (match) perRow = parseInt(match[1], 10) || perRow;
        }
        const aboveFold = Math.max(2, perRow * 2);
        sources = Array.from(
          parsedContainer.querySelectorAll('.card-image-slider__list .slider__slide:first-child img')
        ).slice(0, aboveFold);
      } catch (e) {
        resolve();
        return;
      }
      if (!sources.length) {
        resolve();
        return;
      }

      const stage = document.createElement('div');
      stage.setAttribute('aria-hidden', 'true');
      stage.style.cssText =
        'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';
      const clones = sources.map((src) => {
        const img = document.createElement('img');
        const srcset = src.getAttribute('srcset');
        const sizes = src.getAttribute('sizes');
        if (srcset) img.setAttribute('srcset', srcset);
        if (sizes) img.setAttribute('sizes', sizes);
        img.setAttribute('src', src.getAttribute('src') || '');
        img.loading = 'eager';
        stage.appendChild(img);
        return img;
      });
      document.body.appendChild(stage);

      const done = () => {
        if (stage.parentNode) stage.remove();
        resolve();
      };
      if (typeof clones[0].decode !== 'function') {
        done();
        return;
      }
      const decoded = Promise.all(clones.map((img) => img.decode().catch(() => {})));
      const cap = new Promise((r) => setTimeout(r, capMs));
      Promise.race([decoded, cap]).then(done);
    });
  }

  // Double-buffer the grid swap (mirrors FacetFiltersForm.swapGridWhenReady on the
  // collection page): keep the current grid visible (dimmed) while the incoming
  // grid loads HIDDEN in the background; once its above-the-fold images decode,
  // reveal the whole new grid at once. No blank gap, no per-image popcorn, no
  // fade-in. Matches: click -> grid dims -> new grid loads in background -> whole
  // new grid appears together.
  /**
   * a11y (WCAG 4.1.1 / axe `duplicate-id`): the grid HTML injected here is
   * rendered by the COLLECTION section, so every id inside it is built from the
   * collection template's section id — not this section's. Two homepage
   * switchers showing the same product therefore inject byte-identical ids into
   * one document (12 such nodes measured on the v19 homepage: CardSlider-*,
   * quickAdd*, quick-add-*-overlay-submit, StandardCardNoMediaLink-*, title-*).
   * Duplicate ids break label/aria targeting and form association — whichever
   * element wins getElementById is arbitrary.
   *
   * Rewrite every id and every attribute that REFERENCES one, while the fragment
   * is still detached (cheap, and never touches live DOM). Nothing in the theme
   * reconstructs these ids from a string template — handlers use closest() or
   * data attributes — so renaming is safe as long as the IDREF attrs move too.
   */
  namespaceIds(root) {
    const prefix = `${this.idNamespace}--`;
    const renamed = new Map();

    root.querySelectorAll('[id]').forEach((el) => {
      const current = el.getAttribute('id');
      if (!current || current.startsWith(prefix)) return;
      const next = prefix + current;
      renamed.set(current, next);
      el.setAttribute('id', next);
    });
    if (!renamed.size) return;

    // Attributes whose value is an id or a space-separated id list.
    ['for', 'form', 'headers', 'list', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach(
      (attr) => {
        root.querySelectorAll(`[${attr}]`).forEach((el) => {
          const mapped = el
            .getAttribute(attr)
            .split(/\s+/)
            .filter(Boolean)
            .map((token) => renamed.get(token) || token)
            .join(' ');
          el.setAttribute(attr, mapped);
        });
      }
    );

    root.querySelectorAll('a[href^="#"]').forEach((el) => {
      const target = el.getAttribute('href').slice(1);
      if (renamed.has(target)) el.setAttribute('href', `#${renamed.get(target)}`);
    });
  }

  swapGrid(parsed, instant = false) {
    if (!this.gridContainer) return;
    const parsedContainer = parsed.getElementById('ProductGridContainer');
    if (!parsedContainer) return;

    parsedContainer.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });

    this.namespaceIds(parsedContainer);

    // Preserve the collection page's full page size (typically 36) so the
    // injected pagination matches the products shown. Slicing here breaks
    // navigation because Shopify only exposes per-page granularity, not
    // arbitrary count slicing — sliced-off products become unreachable.
    const newHTML = parsedContainer.innerHTML;
    const container = this.gridContainer;
    const generation = ++this.swapGeneration;

    const apply = () => {
      // Capture the scroll position + the page's non-grid height BEFORE the swap,
      // while the outgoing (taller) page is still laid out. This grid is the
      // page's last meaningful content — the sections below it are disabled — so
      // swapping in a SHORTER category shrinks the whole document. If scrollY then
      // exceeds the shorter page's max, the browser clamps it upward: the page
      // "jumps" and the MENS/WOMENS nav slides up behind the image row above it.
      // We reserve grid height below so the document can't shrink past the current
      // view, so no clamp happens. These reads must come first — reading layout
      // AFTER the innerHTML shrink would force the clamp before our reserve lands,
      // baking in the very jump we're preventing.
      const anchorScroll = window.scrollY;
      const nonGridHeight = document.documentElement.scrollHeight - container.offsetHeight;

      // Remove the swap state BEFORE inserting so the incoming #product-grid is
      // created at full opacity and appears at once with NO fade-in. The fresh
      // .collection has no .loading class, so the swap also drops the loading
      // overlay ATOMICALLY with the new grid (no flicker); clear aria-busy too,
      // since innerHTML leaves the container's own attributes untouched.
      container.classList.remove('is-swapping');
      container.innerHTML = newHTML;
      container.setAttribute('aria-busy', 'false');
      // Re-apply first-row eager loading to the freshly swapped-in grid.
      this.upgradeAboveFoldImages();

      // Reserve in the SAME synchronous block as the innerHTML shrink, with no
      // layout read in between, so the browser lays out once — already tall enough
      // — and never clamps scrollY. min-height is a floor: a taller category
      // overflows it (no-op), and images decoding in later only grow the grid past
      // it. Clear it outright when no reserve is needed so it never lingers.
      const minHeight = anchorScroll + window.innerHeight - nonGridHeight;
      container.style.minHeight = minHeight > 0 ? `${minHeight}px` : '';
    };

    // Cached render: the section HTML + image BYTES are warm, but the freshly-created
    // decoding="async" <img>s still decode off the main thread, so a plain innerHTML
    // swap paints them blank-then-image (popcorn). Pre-decode the above-the-fold
    // images first (cheap — bytes are HTTP-cached) so the cached grid reveals
    // already-painted. No dim: the outgoing grid stays fully visible for the brief,
    // capped decode, so there's no loading flash.
    if (instant) {
      this.preloadAboveFold(parsedContainer, 300).then(() => {
        if (generation !== this.swapGeneration) return;
        apply();
      });
      return;
    }

    container.classList.add('is-swapping'); // dim the outgoing grid (stays on screen — no blank)
    // Preload + decode the incoming above-the-fold images, then reveal the whole grid
    // at once. Generous cap: the old grid stays visible (dimmed) the whole time, so
    // this only bounds a pathologically slow/broken image.
    this.preloadAboveFold(parsedContainer, 1500).then(() => {
      // A newer swap superseded this one (user clicked again during the load) — drop
      // this stale result instead of overwriting the newer grid.
      if (generation !== this.swapGeneration) return;
      apply();
    });
  }

  setLoading(isLoading) {
    if (!this.gridContainer) return;
    this.gridContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    const collection = this.gridContainer.querySelector('.collection');
    if (collection) collection.classList.toggle('loading', isLoading);
  }

  // Pagination links live inside the AJAX-swapped grid. Use delegation on the
  // grid container so we don't need to rebind after every swap.
  onPaginationClick(event) {
    const link = event.target.closest('a.pagination__item');
    if (!link) return;
    if (link.getAttribute('aria-disabled') === 'true') return;
    const href = link.getAttribute('href');
    if (!href) return;

    event.preventDefault();

    try {
      const parsedUrl = new URL(href, window.location.origin);
      // fetchAndSwap re-adds the right section_id for the active collection.
      parsedUrl.searchParams.delete('section_id');
      this.fetchAndSwap(parsedUrl.searchParams.toString());
      this.scrollToTop();
    } catch (e) {
      console.error('homepage-collection-experience pagination click failed:', e);
    }
  }

  scrollToTop() {
    const rect = this.getBoundingClientRect();
    const top = rect.top + window.scrollY - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

if (!customElements.get('homepage-collection-experience')) {
  customElements.define('homepage-collection-experience', HomepageCollectionExperience);
}
